import { useCallback, useEffect, useRef, useState } from 'react';

import type { Batch, Order } from '@notify/shared';
import { BatchStatus } from '@notify/shared';

import { shopService } from '../../services/shop.service';

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastItem { id: number; type: 'success' | 'error'; message: string; }

function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const show = useCallback((type: ToastItem['type'], message: string) => {
    const id = Date.now();
    setToasts((p) => [...p, { id, type, message }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(d: Date | null | string): string {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ─── New Batch modal ──────────────────────────────────────────────────────────

interface NewBatchModalProps {
  onClose: () => void;
  onCreate: (batch: Batch) => void;
  onError: (msg: string) => void;
}

function NewBatchModal({ onClose, onCreate, onError }: NewBatchModalProps) {
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setSubmitting(true);
    try {
      const batch = await shopService.createBatch(label.trim());
      onCreate(batch);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create batch');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-base font-semibold text-[#0F172A]">New Batch</h2>
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-4">
          <div>
            <label htmlFor="batch-label" className="block text-sm font-medium text-[#0F172A]">
              Batch Label
            </label>
            <input
              ref={inputRef}
              id="batch-label"
              type="text"
              placeholder="e.g. Lunch batch 1"
              className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !label.trim()}
              className="flex-1 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Batch card ───────────────────────────────────────────────────────────────

interface BatchCardProps {
  batch: Batch;
  orders: Order[];
  onNotify: (batchId: string) => Promise<void>;
  notifying: boolean;
}

function BatchCard({ batch, orders, onNotify, notifying }: BatchCardProps) {
  const isOpen = batch.status === BatchStatus.OPEN;

  async function handleNotify() {
    const msg = `Send notification to ${orders.length} customer${orders.length === 1 ? '' : 's'}?`;
    if (!window.confirm(msg)) return;
    await onNotify(batch.id);
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-[#0F172A]">{batch.label}</h3>
          <p className="mt-0.5 text-xs text-[#64748B]">
            Created {formatTime(batch.createdAt)}
            {batch.notifiedAt && ` · Notified ${formatTime(batch.notifiedAt)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-[#0F172A]">
            {orders.length} order{orders.length === 1 ? '' : 's'}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
              isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {isOpen ? 'Open' : 'Notified'}
          </span>
        </div>
      </div>

      {/* Order pills */}
      {orders.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {orders.map((o) => (
            <span
              key={o.id}
              className="rounded-full bg-[#F1F5F9] px-2.5 py-0.5 font-mono text-xs text-[#0F172A]"
            >
              {o.orderReference}
            </span>
          ))}
        </div>
      )}

      {/* Notify button */}
      {isOpen && (
        <button
          type="button"
          disabled={notifying || orders.length === 0}
          onClick={() => void handleNotify()}
          className="mt-4 w-full rounded-lg bg-[#6366F1] px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-600 disabled:opacity-50 transition-colors"
        >
          {notifying ? 'Sending…' : `Notify All (${orders.length})`}
        </button>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShopBatchesPage() {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [notifying, setNotifying] = useState<string | null>(null);
  const { toasts, show: showToast } = useToast();

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    void (async () => {
      try {
        const [batchesData, ordersData] = await Promise.all([
          shopService.getBatches(),
          shopService.getOrders({ date: today }),
        ]);
        setBatches(batchesData);
        setOrders(ordersData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load batches');
      } finally {
        setLoading(false);
      }
    })();
  }, [today]);

  async function handleNotify(batchId: string) {
    setNotifying(batchId);
    try {
      const result = await shopService.notifyBatch(batchId);
      setBatches((prev) => prev.map((b) => (b.id === batchId ? result.batch : b)));
      showToast('success', `Notified ${result.orders_notified} customer${result.orders_notified === 1 ? '' : 's'}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to send notifications');
    } finally {
      setNotifying(null);
    }
  }

  function handleBatchCreated(batch: Batch) {
    setBatches((prev) => [batch, ...prev]);
    showToast('success', `Batch "${batch.label}" created`);
  }

  // Group orders by batchId
  const ordersByBatch = orders.reduce<Record<string, Order[]>>((acc, o) => {
    if (o.batchId) {
      acc[o.batchId] = [...(acc[o.batchId] ?? []), o];
    }
    return acc;
  }, {});

  return (
    <>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#64748B]">{batches.length} batch{batches.length === 1 ? '' : 'es'} today</p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 transition-colors"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
              <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
            </svg>
            New Batch
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
        )}

        {/* Batches */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-36 animate-pulse rounded-xl bg-gray-200" />
            ))}
          </div>
        ) : batches.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
            <svg className="mb-3 h-10 w-10 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
            <p className="text-sm font-medium text-[#0F172A]">No batches yet</p>
            <p className="mt-0.5 text-xs text-[#64748B]">Create a batch to group orders and notify customers together.</p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="mt-4 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 transition-colors"
            >
              Create first batch
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {batches.map((batch) => (
              <BatchCard
                key={batch.id}
                batch={batch}
                orders={ordersByBatch[batch.id] ?? []}
                onNotify={handleNotify}
                notifying={notifying === batch.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* New Batch modal */}
      {modalOpen && (
        <NewBatchModal
          onClose={() => setModalOpen(false)}
          onCreate={handleBatchCreated}
          onError={(msg) => showToast('error', msg)}
        />
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
          {toasts.map((t) => (
            <div key={t.id} className={`rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${t.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
